import { DiagramNode, DiagramLink, ItemInfo, ItemInputDetail, Bounds } from '../Diagram.types';
import { useRef, useState } from 'preact/hooks';
import { isEqualItem, getNavigableInfo, zoomAndCenterItemFromInfo } from '../utils/diagramUtils';
import { getRandomId } from '../../utils/PRIVATE_visLayoutUtils/LayoutUtils';
import { getNodeDetailFromInfo, getLinkDetailFromInfo } from '../utils/navUtils';
import { useDiagramNavigation } from './useDiagramNavigation';
import type { NavDirection } from '../utils/navUtils';

const DEFAULT_KB_PAN = 15;
const DEFAULT_KB_ZOOM = 0.15;

export function useEvents<K1, K2>(
  initNode: DiagramNode<K1>,
  nodes: DiagramNode<K1>[],
  links: DiagramLink<K2, K1>[],
  nodeBounds: Bounds[],
  linkIdToDataMap: Map<K2, DiagramLink<K2, K1>>,
  nodeIdToIndexMap: Map<K1, number>,
  width: number,
  height: number,
  maxZoom: number,
  onInput?: (detail: ItemInputDetail<K1, K2>) => void,
  kbPanCallback?: (props: {
    dPan?: { dx: number; dy: number };
    nPan?: { cx: number; cy: number };
    center?: boolean;
  }) => void,
  kbZoomCallback?: (props: { dZoom?: number; nZoom?: number }) => void
) {
  const {
    getNextNavigableNode,
    getNextNavigableLink,
    navigateFromNodeToLink,
    navigateFromLinkToNode
  } = useDiagramNavigation(nodes, links, nodeBounds, linkIdToDataMap);
  const [focusedItemInfo, setfocusedItemInfo] = useState<ItemInfo<K1, K2>>({
    id: initNode.id,
    isNode: true
  });
  const [hoveredItemInfo, sethoveredItemInfo] = useState<ItemInfo<K1, K2>>();
  const activeId = useRef<string>();

  const cancelEvent = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const keyDownHandler = (event: KeyboardEvent) => {
    const key = event.key;
    let eventConsumed = false;
    // tabbing out of the diagram
    if (key === 'TAB') {
      return;
    }
    // if this a switch from node to link or vice versa
    if (isChangeItemType(event)) {
      const nextItemInfo = focusedItemInfo.isNode
        ? navigateFromNodeToLink(links, event, getNodeDetailFromInfo(focusedItemInfo, nodes))
        : navigateFromLinkToNode(focusedItemInfo.id as K2, event);
      handleKeyboardInput(nextItemInfo);
      eventConsumed = true;
    } else if (isNavigationEvent(key)) {
      const nextItemInfo = handleKeyboardNavigationEvent(key);
      handleKeyboardInput(nextItemInfo);
      eventConsumed = true;
    } else {
      switch (key) {
        case 'PageUp': {
          kbPanCallback &&
            kbPanCallback({
              dPan: {
                dx: event.shiftKey ? -DEFAULT_KB_PAN : 0,
                dy: event.shiftKey ? 0 : -DEFAULT_KB_PAN
              }
            });
          eventConsumed = true;
          break;
        }
        case 'PageDown': {
          kbPanCallback &&
            kbPanCallback({
              dPan: {
                dx: event.shiftKey ? DEFAULT_KB_PAN : 0,
                dy: event.shiftKey ? 0 : DEFAULT_KB_PAN
              }
            });
          eventConsumed = true;
          eventConsumed = true;
          break;
        }
        case '+':
        case '=': {
          kbZoomCallback && kbZoomCallback({ dZoom: DEFAULT_KB_ZOOM });
          eventConsumed = true;
          break;
        }
        case '-':
        case '_': {
          kbZoomCallback && kbZoomCallback({ dZoom: -DEFAULT_KB_ZOOM });
          eventConsumed = true;
          break;
        }
        case '0': {
          if (event.ctrlKey && event.altKey) {
            const { centerX, centerY, zoom } = zoomAndCenterItemFromInfo(
              focusedItemInfo,
              linkIdToDataMap,
              nodeBounds,
              nodeIdToIndexMap,
              width,
              height,
              maxZoom
            );
            kbZoomCallback && kbZoomCallback({ nZoom: zoom });
            kbPanCallback && kbPanCallback({ nPan: { cx: centerX, cy: centerY } });
          } else {
            // zoom to fit
            kbZoomCallback && kbZoomCallback({});
            kbPanCallback && kbPanCallback({ center: true });
          }
          eventConsumed = true;
        }
      }
    }
    if (eventConsumed) {
      cancelEvent(event);
    }
  };

  const keyUpHandler = (event: KeyboardEvent) => {
    const key = event.code;
    switch (key) {
      case 'Tab': {
        updatefocusedItemInfo({
          ...focusedItemInfo,
          isCurrent: true,
          isFocusVisible: true
        });
        break;
      }
    }
  };

  const blurHandler = () => {
    if (hoveredItemInfo != null || focusedItemInfo.isCurrent) {
      onInput?.({ id: undefined });
    }
    const itemInfo = { ...focusedItemInfo, isCurrent: false, isFocusVisible: false };
    setfocusedItemInfo(itemInfo);
  };

  const hoverHandler = (event: PointerEvent) => {
    const itemInfo = getNavigableInfo<K1, K2>(event.target as HTMLElement);
    if (itemInfo && !isEqualItem(itemInfo, hoveredItemInfo)) {
      sethoveredItemInfo({ ...itemInfo, isCurrent: true });
      activeId.current = getRandomId();
      setfocusedItemInfo({ ...focusedItemInfo, isCurrent: false });
      onInput?.({ id: itemInfo.id, type: itemInfo.isNode ? 'node' : 'link' });
    }
  };

  const pointerLeaveHandler = () => {
    sethoveredItemInfo(undefined);
    activeId.current = undefined;
    onInput?.({ id: undefined });
  };

  const pointerUpHandler = (event: PointerEvent) => {
    const itemInfo = getNavigableInfo<K1, K2>(event.target as HTMLElement);
    if (itemInfo.id != null) {
      setfocusedItemInfo(itemInfo);
      activeId.current = getRandomId();
    }
  };

  function updatefocusedItemInfo(itemInfo: ItemInfo<K1, K2>) {
    onInput?.({ id: itemInfo.id, type: itemInfo.isNode ? 'node' : 'link' });
    activeId.current = getRandomId();
    setfocusedItemInfo(itemInfo);
  }

  function handleKeyboardInput(item?: ItemInfo<K1, K2>) {
    if (!item) return;
    if (!isEqualItem(item, focusedItemInfo)) {
      item.isCurrent = true;
      item.isFocusVisible = true;
      if (hoveredItemInfo) {
        sethoveredItemInfo({ ...hoveredItemInfo, isCurrent: false });
      }
      updatefocusedItemInfo(item);
    }
  }

  function isNavigationEvent(key: string) {
    switch (key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'ArrowRight':
      case 'ArrowLeft': {
        return true;
      }
    }
    return false;
  }

  function isChangeItemType(event: KeyboardEvent) {
    const key = event.code;
    // check if switching from node to link
    // equivalent of ≤ and ≥
    if (focusedItemInfo.isNode && event.altKey && (key === 'Period' || key === 'Comma')) {
      return true;
      // check if switching from link to node
    } else if (!focusedItemInfo.isNode && (key === 'ArrowLeft' || key === 'ArrowRight')) {
      return true;
    }
    return false;
  }

  function handleKeyboardNavigationEvent(key: string) {
    let direction: NavDirection = 'down';
    switch (key) {
      case 'ArrowDown': {
        direction = 'down';
        break;
      }
      case 'ArrowUp': {
        direction = 'up';
        break;
      }
      case 'ArrowRight': {
        direction = 'right';
        break;
      }
      case 'ArrowLeft': {
        direction = 'left';
        break;
      }
    }
    if (focusedItemInfo.isNode) {
      const itemDetail = getNextNavigableNode(
        direction,
        true,
        getNodeDetailFromInfo(focusedItemInfo, nodes),
        nodes
      );
      return itemDetail ? { id: itemDetail.id, isNode: true } : focusedItemInfo;
    } else {
      const linkDetail = getLinkDetailFromInfo(focusedItemInfo, links);
      const itemDetail = getNextNavigableLink(direction, nodes, linkDetail, links);
      return itemDetail ? { id: itemDetail.id } : { id: focusedItemInfo.id };
    }
  }

  return {
    focusedItemInfo,
    hoveredItemInfo,
    'aria-activedescendant': activeId.current,
    onKeyUp: keyUpHandler,
    onKeyDown: keyDownHandler,
    onBlur: blurHandler,
    onPointerUp: pointerUpHandler,
    onPointerMove: hoverHandler,
    onPointerLeave: pointerLeaveHandler
  };
}
